import { ChangeEvent, useCallback, useRef } from 'react';
import classnames from 'classnames';

import InputWrapper, {
  IInputWrapperProps,
} from '~/components/atoms/FormControls/InputWrapper';
import ButtonClean from '~/components/ButtonClean';

import FileImage from './FileImage';

import styles from './FileInput.module.scss';

type TFileInput = IInputWrapperProps &
  Omit<JSX.IntrinsicElements['input'], 'value'> & {
    inputWrapperClassName?: string;
    buttonTranslation: string;
    value?: Array<File>;
    onChange: (files: Array<File> | undefined) => void;
    text?: string;
  };

export default function FileInput({
  error,
  id,
  label,
  errorAction,
  className,
  inputWrapperClassName,
  buttonTranslation,
  onChange,
  value,
  text,
  ...inputProps
}: TFileInput): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  const onClickWrapper = useCallback(() => {
    if (ref.current) {
      ref.current.click();
    }
  }, []);

  const onFilesChangeCallback = useCallback(
    ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
      onChange(Array.from(files || []));
    },
    [onChange],
  );

  const removeFile = useCallback(
    (fileRemoved: File) => {
      onChange(value?.filter((file) => file !== fileRemoved));
    },
    [onChange, value],
  );

  return (
    <InputWrapper
      label={label}
      errorAction={errorAction}
      error={error}
      id={id}
      className={classnames(styles.wrapper, inputWrapperClassName)}
    >
      {!value?.length ? (
        <ButtonClean
          className={classnames(styles.fileButton, error && styles.error)}
          onClick={onClickWrapper}
        >
          {`${buttonTranslation} +`}
        </ButtonClean>
      ) : (
        <div className={styles.imagesWrapper}>
          {value.map((file) => (
            <FileImage
              key={file.name}
              file={file}
              onClick={removeFile}
            />
          ))}
        </div>
      )}
      <input
        accept=".png, .jpg, .jpeg"
        multiple
        {...inputProps}
        id={id}
        onChange={onFilesChangeCallback}
        ref={ref}
        style={{ display: 'none' }}
        type="file"
      />
      {text && <div className={styles.description}>{text}</div>}
    </InputWrapper>
  );
}
